import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// Self-registered so the codec never depends on import order (same
// self-registration pattern as LiveMapStoreNew).
dayjs.extend(utc);
dayjs.extend(timezone);

// Persisted marker for a single-day range equal to the day it was stored on.
// Re-resolved to the current day on retrieval, so a pinned "today" selection
// follows the calendar instead of freezing the date it was persisted on. Same
// convention as the Dispatch page's ordersDispatch_appliedFilters payload.
const TODAY_DATE_STORE_KEY = 'today';

type DateRangeFields = {
  endDate?: string;
  startDate?: string;
};

// Stored payload contract: literal nulls mean the user explicitly chose
// "All dates"; an absent storage key means "never chose here", which lets the
// consuming page fall back to its own default range.
type StoredDateRange = {
  endDate: string | null;
  startDate: string | null;
};

function encodeDateRangeSentinel(range: DateRangeFields) {
  const rangeIsSingleDay = Boolean(range.startDate) && range.startDate === range.endDate;
  // Compared in the app default timezone — the same clock decode resolves the
  // sentinel with — so encode('today') and decode('today') can never disagree.
  // (The isToday plugin would compare against the browser-local date instead.)
  const rangeIsCurrentDay =
    rangeIsSingleDay && range.startDate === dayjs.tz().format('YYYY-MM-DD');

  if (rangeIsCurrentDay) {
    return { endDate: TODAY_DATE_STORE_KEY, startDate: TODAY_DATE_STORE_KEY };
  }

  return range;
}

function decodeDateRangeSentinel(range: DateRangeFields) {
  const decoded = { ...range };

  if (decoded.startDate === TODAY_DATE_STORE_KEY) {
    decoded.startDate = dayjs.tz().format('YYYY-MM-DD');
  }

  if (decoded.endDate === TODAY_DATE_STORE_KEY) {
    decoded.endDate = dayjs.tz().format('YYYY-MM-DD');
  }

  return decoded;
}

export function persistDateRange(storageKey: string, range: StoredDateRange) {
  const encoded = encodeDateRangeSentinel({
    endDate: range.endDate ?? undefined,
    startDate: range.startDate ?? undefined,
  });

  localStorage.setItem(
    storageKey,
    JSON.stringify({
      endDate: encoded.endDate ?? null,
      startDate: encoded.startDate ?? null,
    }),
  );
}

export function retrievePersistedDateRange(storageKey: string) {
  const storedValue = localStorage.getItem(storageKey);

  if (storedValue === null) {
    return undefined;
  }

  // localStorage is untyped, user-editable input: anything that is not the
  // exact StoredDateRange shape reads as "nothing stored".
  let parsedValue: unknown;
  try {
    parsedValue = JSON.parse(storedValue);
  } catch {
    return undefined;
  }

  if (parsedValue === null || typeof parsedValue !== 'object') {
    return undefined;
  }

  const { endDate, startDate } = parsedValue as Partial<StoredDateRange>;
  const endDateIsValid = endDate === null || typeof endDate === 'string';
  const startDateIsValid = startDate === null || typeof startDate === 'string';

  if (!endDateIsValid || !startDateIsValid) {
    return undefined;
  }

  const decoded = decodeDateRangeSentinel({
    endDate: endDate ?? undefined,
    startDate: startDate ?? undefined,
  });

  return { endDate: decoded.endDate ?? null, startDate: decoded.startDate ?? null };
}
